import { useState } from 'react';

export interface PinListInputProps {
	/** The pins currently committed for the row. */
	pins: string[];
	/** Called with the parsed pins whenever the text changes. */
	onChange: (pins: string[]) => void;
	/** Class applied to the input element. */
	className?: string;
}

/**
 * Splits comma-separated text into trimmed, non-empty pin names.
 *
 * @param text - The text as typed.
 * @returns The pin names in order.
 */
function parsePinList(text: string): string[] {
	return text
		.split(',')
		.map((pin) => pin.trim())
		.filter(Boolean);
}

/**
 * Text input for a comma-separated pin list. The typed text is kept as-is while editing, so a
 * trailing comma or space survives until the next pin is typed; the text is replaced only when
 * `pins` changes to something the text does not already describe.
 *
 * @param props - The committed pins, the change handler and the input's class.
 * @returns The input element.
 */
export function PinListInput({ pins, onChange, className }: PinListInputProps) {
	const [text, setText] = useState(() => pins.join(', '));

	// Both sides go through the parser so pins it would rewrite (padded, empty, comma-bearing)
	// still match the reset text; comparing against raw `pins` would reset on every render.
	const describesPins = parsePinList(text).join(',') === parsePinList(pins.join(',')).join(',');
	if (!describesPins) {
		setText(pins.join(', '));
	}

	return (
		<input
			type='text'
			value={describesPins ? text : pins.join(', ')}
			onChange={(e) => {
				setText(e.target.value);
				onChange(parsePinList(e.target.value));
			}}
			className={className}
		/>
	);
}
